// filtri/stabilizza.ts — toglie il tremolio ai punti del volto senza farli "inseguire" in ritardo.
// Filtro "One Euro" (Casiez, Roussel, Vogel 2012): fermo quando il volto è fermo, pronto quando si muove.
// Puro, senza DOM: si prova con node:test. Lo usa il motore, non i filtri.

const DUE_PI = Math.PI * 2;
const alfa = (tagliaHz: number, dtS: number): number => {
  const tau = 1 / (DUE_PI * tagliaHz);
  return 1 / (1 + tau / dtS);
};

export interface ParametriStabilizza {
  /** Frequenza di taglio a riposo (Hz): più bassa = più fermo, ma più lento. */
  tagliaMinima: number;
  /** Quanto la velocità alza la frequenza di taglio (per pixel al secondo). */
  beta: number;
  /** Frequenza di taglio della stima della velocità (Hz). */
  tagliaDerivata: number;
}

export const STABILIZZA_PREDEFINITO: ParametriStabilizza = { tagliaMinima: 1, beta: 0.04, tagliaDerivata: 1 };

/** Stabilizza un vettore di numeri a lunghezza fissa (es. 478 punti × x,y,z). */
export class Stabilizzatore {
  private valori: Float64Array | null = null;
  private derivate: Float64Array | null = null;
  private ultimoMs = 0;
  private p: ParametriStabilizza;
  constructor(p: ParametriStabilizza = STABILIZZA_PREDEFINITO) {
    this.p = p;
  }

  /** Da chiamare quando il volto sparisce: alla ricomparsa si riparte senza "scivolare" dalla posizione vecchia. */
  azzera(): void {
    this.valori = null;
    this.derivate = null;
  }

  /** Restituisce un NUOVO array stabilizzato. `tMs` deve crescere; `scala` normalizza la velocità (distanza occhi). */
  applica(grezzi: ArrayLike<number>, tMs: number, scala = 100): Float64Array {
    const n = grezzi.length;
    if (!this.valori || this.valori.length !== n || tMs <= this.ultimoMs) {
      this.valori = Float64Array.from(grezzi);
      this.derivate = new Float64Array(n);
      this.ultimoMs = tMs;
      return Float64Array.from(this.valori);
    }
    const dt = Math.min(0.2, (tMs - this.ultimoMs) / 1000);
    this.ultimoMs = tMs;
    const aD = alfa(this.p.tagliaDerivata, dt);
    // La velocità si misura in "distanze tra gli occhi al secondo": così vale per volti vicini e lontani.
    const norm = 100 / Math.max(1, scala);
    const v = this.valori;
    const d = this.derivate!;
    for (let i = 0; i < n; i++) {
      const x = grezzi[i]!;
      const dx = (x - v[i]!) / dt;
      d[i] = d[i]! + aD * (dx - d[i]!);
      const taglia = this.p.tagliaMinima + this.p.beta * Math.abs(d[i]!) * norm;
      v[i] = v[i]! + alfa(taglia, dt) * (x - v[i]!);
    }
    return Float64Array.from(v);
  }
}
